import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  ArrowRight, ArrowLeft, Send, Loader2, Globe2, BadgeCheck, Sparkles, ShieldCheck, Mail, ClipboardCheck,
  UserCheck, Crown, Search, Building2, Activity, RotateCcw, AlertTriangle, Radar,
  Square, TrendingUp, Lightbulb, Zap, CalendarClock,
} from "lucide-react";
import { api, formatApiErrorDetail } from "../lib/api";
import { useAuth } from "../context/AuthContext";

const PIPELINE = [
  [Search, "SOURCE DISCOVERY"], [Globe2, "WEBSITE VERIFIED"], [Sparkles, "10X AI SCORE"],
  [ShieldCheck, "ADMIN REVIEW"], [Mail, "INVITATION SENT"], [Building2, "LANDING PAGE"],
  [ClipboardCheck, "REQUEST SUBMITTED"], [BadgeCheck, "OTP VERIFIED"], [UserCheck, "ADMIN APPROVAL"], [Crown, "10X MEMBER"],
];

const STATUS_CLS = {
  DISCOVERED: "text-slate-300 border-slate-600", VERIFIED: "text-techblue-light border-techblue/40 bg-techblue/10",
  QUALIFIED: "text-emerald-400 border-emerald-500/40 bg-emerald-500/10",
  VERIFICATION_FAILED: "text-red-400 border-red-500/40 bg-red-500/10",
  EMAIL_FAILED: "text-red-400 border-red-500/40 bg-red-500/10",
  NEEDS_CONTACT: "text-amber-400 border-amber-500/40 bg-amber-500/10",
  ADMIN_REVIEW: "text-violet-300 border-violet-400/40 bg-violet-400/10",
  INVITED: "text-techblue-light border-techblue/40 bg-techblue/10",
  VIEWED: "text-cyan-300 border-cyan-400/40 bg-cyan-400/10",
  REQUESTED: "text-amber-400 border-amber-500/40 bg-amber-500/10",
  MEMBER: "text-gold border-gold/50 bg-gold/10",
};

const COUNTRIES = ["India", "USA", "UK", "UAE", "Saudi Arabia", "Singapore", "Europe", "Asia-Pacific", "Global"];
const INDUSTRIES = ["Manufacturing", "Pharma", "Healthcare", "Renewable Energy", "Solar", "EV", "Battery", "Logistics", "Warehousing", "Food Processing", "Agro", "Infrastructure", "Real Estate", "Education", "Technology", "Engineering", "Chemicals", "Exporters", "Importers", "Other"];
const SIZES = ["Startup", "SME", "Mid-Market", "Enterprise", "Large Corporate"];

const STAGE_MATCH = {
  "SOURCE DISCOVERY": () => true,
  "WEBSITE VERIFIED": (p) => p.website_verified,
  "10X AI SCORE": (p) => (p.ai_score || 0) > 0,
  "ADMIN REVIEW": (p) => p.status === "ADMIN_REVIEW",
  "INVITATION SENT": (p) => ["INVITED", "VIEWED", "REQUESTED", "MEMBER"].includes(p.status),
  "LANDING PAGE": (p) => ["VIEWED", "REQUESTED", "MEMBER"].includes(p.status),
  "REQUEST SUBMITTED": (p) => ["REQUESTED", "MEMBER"].includes(p.status),
  "OTP VERIFIED": (p) => p.status === "MEMBER",
  "ADMIN APPROVAL": (p) => p.status === "MEMBER",
  "10X MEMBER": (p) => p.status === "MEMBER",
};

const EVENT_ICON = (t) => t.startsWith("website") ? Globe2 : t.startsWith("ai") ? Sparkles : t.startsWith("email") || t.startsWith("invitation") ? Mail : t.startsWith("otp") ? ShieldCheck : t.startsWith("member") ? Crown : t.startsWith("pipeline") ? RotateCcw : Building2;

export default function AdminOutreach() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [prospects, setProspects] = useState(null);
  const [settings, setSettings] = useState(null);
  const [metrics, setMetrics] = useState(null);
  const [activity, setActivity] = useState([]);
  const [failures, setFailures] = useState([]);
  const [form, setForm] = useState({ company_name: "", website: "", industry: "", contact_email: "" });
  const [batch, setBatch] = useState("");
  const [govRows, setGovRows] = useState("");
  const [govSource, setGovSource] = useState("APEDA");
  const [growthQuery, setGrowthQuery] = useState("");
  const [run, setRun] = useState(null);
  const [runs, setRuns] = useState([]);
  const [recs, setRecs] = useState(null);
  const [forecast, setForecast] = useState(null);
  const [stageFilter, setStageFilter] = useState("");
  const [disc, setDisc] = useState({ country: "India", industry: "Manufacturing", size: "SME", filters: "" });
  const [working, setWorking] = useState("");
  const [inviting, setInviting] = useState("");
  const [contactDraft, setContactDraft] = useState({});

  const load = useCallback(() => {
    api.get("/admin/prospects").then((r) => setProspects(r.data)).catch(() => navigate("/admin/login"));
    api.get("/admin/settings").then((r) => setSettings(r.data)).catch(() => {});
    api.get("/admin/acquisition/metrics").then((r) => setMetrics(r.data)).catch(() => {});
    api.get("/admin/activity").then((r) => setActivity(r.data)).catch(() => {});
    api.get("/admin/failures").then((r) => setFailures(r.data)).catch(() => {});
    api.get("/admin/runs").then((r) => setRuns(r.data)).catch(() => {});
    api.get("/admin/recommendations").then((r) => setRecs(r.data)).catch(() => {});
    api.get("/admin/forecast").then((r) => setForecast(r.data)).catch(() => {});
  }, [navigate]);

  useEffect(() => { if (user === false) navigate("/admin/login"); }, [user, navigate]);
  useEffect(() => {
    if (!user) return;
    load();
    const t = setInterval(load, 8000);
    return () => clearInterval(t);
  }, [user, load]);

  const toggle = async (key, value) => {
    try {
      const { data } = await api.post("/admin/settings", { [key]: value });
      setSettings(data);
      toast.success("Autopilot updated");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const addProspect = async (e) => {
    e.preventDefault();
    setWorking("single");
    try {
      const { data } = await api.post("/admin/prospects", form);
      toast.success(data.status === "INVITED"
        ? `${data.company_name} — verified, scored ${data.ai_score}/100, invitation sent`
        : `${data.company_name} — status: ${data.status}`);
      setForm({ company_name: "", website: "", industry: "", contact_email: "" });
      load();
    } catch (err) {
      if (err.response?.status === 409) toast.warning(err.response.data.detail);
      else toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally { setWorking(""); }
  };

  const runBatch = async () => {
    if (!batch.trim()) return;
    setWorking("batch");
    try {
      const { data } = await api.post("/admin/prospects/batch", { lines: batch });
      toast.success(`${data.queued} companies queued — pipeline running automatically`);
      setBatch("");
      setTimeout(load, 4000);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setWorking(""); }
  };

  const runGovImport = async () => {
    if (!govRows.trim()) return;
    setWorking("gov");
    try {
      const { data } = await api.post("/admin/sources/gov-import", { source: govSource, rows: govRows });
      toast.success(`${data.rows} ${govSource} companies queued — verify → enrich → score → invite runs automatically`);
      setGovRows("");
      setTimeout(load, 4000);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setWorking(""); }
  };

  const syncMpeda = async () => {
    setWorking("mpeda");
    try {
      await api.post("/admin/sources/mpeda-sync");
      toast.success("MPEDA registry auto-sync started — government-registered seafood exporters are being verified & imported automatically");
      setTimeout(load, 4000);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setWorking(""); }
  };

  const runGrowth = async () => {
    if (!growthQuery.trim()) return;
    setWorking("growth");
    try {
      const { data } = await api.post("/admin/growth/run", { query: growthQuery });
      toast.success(`Growth engine running — ${data.industry} / ${data.country} via ${data.source === "google" ? "GOOGLE PLACES" : "10X AI"}. Pipeline is fully automatic.`);
      setGrowthQuery("");
      if (data.run_id) pollRun(data.run_id);
      setTimeout(load, 4000);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setWorking(""); }
  };

  const pollRun = useCallback(async (runId) => {
    try {
      const { data } = await api.get(`/admin/runs/${runId}`);
      setRun(data);
      if (data.status === "RUNNING") setTimeout(() => pollRun(runId), 2500);
      else load();
    } catch { /* stop polling */ }
  }, [load]);

  const stopRun = async (runId) => {
    try {
      await api.post(`/admin/runs/${runId}/stop`);
      toast.success("Campaign stop requested — the pipeline halts at the next company.");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const retryAll = async () => {
    setWorking("retryall");
    try {
      await api.post("/admin/failures/retry-all");
      toast.success("Bulk retry started — up to 20 failures reprocessed with 2s backoff.");
      setTimeout(load, 4000);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setWorking(""); }
  };

  const runRecAction = async (key) => {
    if (key === "review-requests") { navigate("/admin"); return; }
    setWorking("rec");
    try {
      await api.post(key === "invite-qualified" ? "/admin/actions/invite-qualified" : "/admin/failures/retry-all");
      toast.success("Action started — pipeline is running automatically.");
      setTimeout(load, 4000);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setWorking(""); }
  };

  const runDiscovery = async () => {
    setWorking("discover");
    try {
      const { data } = await api.post("/admin/discover", disc);
      toast.success(`Discovery complete: ${data.created} new companies verified & scored (${data.skipped_duplicates} duplicates skipped)`);
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setWorking(""); }
  };

  const invite = async (token, company) => {
    setInviting(token);
    try {
      await api.post(`/admin/prospects/${token}/invite`);
      toast.success(`Invitation emailed to ${company}`);
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setInviting(""); }
  };

  const retry = async (token) => {
    setInviting(token);
    try {
      const { data } = await api.post(`/admin/failures/${token}/retry`);
      toast.success(`Retry complete — status: ${data.status}`);
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setInviting(""); }
  };

  const saveContact = async (token) => {
    const email = (contactDraft[token] || "").trim();
    if (!email) return;
    setInviting(token);
    try {
      const { data } = await api.post(`/admin/prospects/${token}/contact`, { email });
      toast.success(`Contact saved — status: ${data.status}`);
      setContactDraft({ ...contactDraft, [token]: "" });
      load();
    } catch (e) {
      if (e.response?.status === 409) toast.warning(e.response.data.detail);
      else toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally { setInviting(""); }
  };

  if (!prospects || !metrics) return <div className="max-w-7xl mx-auto px-8 py-24 text-sm font-mono text-slate-500 animate-pulse">Loading acquisition command center…</div>;

  const visibleProspects = stageFilter && prospects ? prospects.filter(STAGE_MATCH[stageFilter]) : prospects || [];

  const METRIC_CARDS = [
    ["Companies Discovered", metrics.discovered, null],
    ["Websites Verified", metrics.verified, metrics.conversion.verification],
    ["Qualified (AI ≥ threshold)", metrics.qualified, metrics.conversion.qualification],
    ["Invitations Sent", metrics.invited, metrics.conversion.invitation],
    ["Membership Requests", metrics.requests, metrics.conversion.request],
    ["10X Members", metrics.members, metrics.conversion.membership],
  ];

  return (
    <div className="max-w-[90rem] mx-auto px-4 md:px-8 py-12" data-testid="admin-outreach">
      {/* HEADER */}
      <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
        <div>
          <p className="gold-kicker mb-2">10XGLOBAL Admin — AI Membership Acquisition Command Center</p>
          <h1 className="font-display text-2xl md:text-3xl font-bold text-slate-100" data-testid="outreach-title">10XGLOBAL ACQUISITION ENGINE</h1>
          <p className="text-xs text-slate-500 mt-1.5">Discover the right companies. Verify the opportunity. Invite automatically. Convert qualified businesses into 10X Members. · Official desk: rathod@10x-global.com</p>
        </div>
        <div className="flex items-center gap-3">
          <span
            data-testid="email-provider-badge"
            title={metrics.email_provider === "live" ? "Sending via your verified Resend domain 10x-global.com — live delivery to any recipient" : "Sandbox mode — add RESEND_API_KEY (verified 10x-global.com domain) to backend env for live delivery"}
            className={`flex items-center gap-1.5 border rounded-sm px-2.5 py-1.5 text-[9px] font-mono uppercase tracking-widest ${metrics.email_provider === "live" ? "border-emerald-500/40 bg-emerald-500/10 text-emerald-400" : "border-amber-500/40 bg-amber-500/10 text-amber-400"}`}
          >
            <Mail size={11} /> EMAIL: {metrics.email_provider === "live" ? "PRODUCTION" : "SANDBOX"}
          </span>
          <Link to="/admin" data-testid="outreach-back-btn" className="btn-outline text-xs"><ArrowLeft size={14} /> BACK TO DASHBOARD</Link>
        </div>
      </div>

      {/* FINAL SYSTEM STATUS */}
      {metrics?.system_status && (
        <div className="flex flex-wrap gap-2 mb-8" data-testid="system-status-strip">
          {Object.entries(metrics.system_status).map(([k, v]) => {
            const good = /READY|ACTIVE|VERIFIED|CONNECTED|RECEIVING|LIVE/.test(String(v));
            return (
              <span key={k} data-testid={`sysstatus-${k}`} className={`flex items-center gap-1.5 border rounded-sm px-2.5 py-1.5 text-[9px] font-mono uppercase tracking-widest ${good ? "border-emerald-500/40 bg-emerald-500/10 text-emerald-400" : "border-amber-500/40 bg-amber-500/10 text-amber-400"}`}>
                <span className={`w-1.5 h-1.5 rounded-full ${good ? "bg-emerald-400" : "bg-amber-400"} animate-pulse`} /> {k}: {String(v).slice(0, 30)}
              </span>
            );
          })}
        </div>
      )}

      {/* MASTER GROWTH CONTROL */}
      <div className="card-surface hairline-gold p-6 md:p-8 mb-8 relative overflow-hidden" data-testid="growth-engine">
        <div className="absolute -top-16 -right-16 w-56 h-56 rounded-full bg-gold/10 blur-3xl pointer-events-none" />
        <p className="gold-kicker mb-2">One-Click Growth Engine</p>
        <h2 className="font-display text-xl md:text-2xl font-bold text-slate-100 mb-1">PASTE A GOOGLE LINK. OR JUST TYPE WHAT YOU NEED.</h2>
        <p className="text-xs text-slate-500 mb-4">Discover → Verify → Score → Invite → Track → Convert</p>
        <div className="flex flex-wrap gap-2 mb-5">
          {["Manufacturing companies in Gujarat", "Renewable energy companies in India", "Pharma exporters with global operations", "500 qualified companies in UAE"].map((ex) => (
            <button key={ex} onClick={() => setGrowthQuery(ex)} data-testid={`growth-example-${ex.split(" ")[0].toLowerCase()}`}
              className="border border-slate-700 hover:border-gold/60 rounded-sm px-2.5 py-1 text-[10px] font-mono text-slate-400 hover:text-gold transition-colors">{ex}</button>
          ))}
        </div>
        <div className="flex flex-col sm:flex-row gap-3">
          <input value={growthQuery} onChange={(e) => setGrowthQuery(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && runGrowth()}
            placeholder='e.g. "seafood exporters in Mumbai" — or paste a google.com/search?q=… link'
            data-testid="growth-query-input" className="input-dark flex-1 !py-3.5 text-sm" />
          <button onClick={runGrowth} disabled={!!working || !growthQuery.trim()} data-testid="growth-run-btn"
            className="btn-gold !px-8 !py-3.5 text-sm font-bold disabled:opacity-60 shrink-0">
            {working === "growth" ? <><Loader2 size={15} className="animate-spin" /> RUNNING…</> : "RUN ENTIRE PIPELINE →"}
          </button>
        </div>
        <p className="text-[10px] text-slate-600 mt-3">Source is automatic: GOOGLE PLACES when GOOGLE_PLACES_API_KEY is configured, otherwise 10X AI. Google search links are parsed automatically. The advanced panels below remain available for power users.</p>
      </div>

      {/* LIVE EXECUTION CONSOLE */}
      {run && (
        <div className="card-surface border-techblue/40 p-5 md:p-6 mb-8" data-testid="execution-console">
          <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
            <p className="text-[10px] font-mono uppercase tracking-widest text-techblue-light flex items-center gap-2">
              <Activity size={13} className={run.status === "RUNNING" ? "animate-pulse" : ""} />
              10X AI ENGINE — {run.status} · {run.name}
            </p>
            {run.status === "RUNNING" && (
              <button onClick={() => stopRun(run.run_id)} data-testid="run-stop-btn" className="btn-outline text-xs !px-4 !py-1.5"><Square size={11} /> STOP</button>
            )}
          </div>
          <div className="space-y-1.5 text-xs font-mono text-slate-400 mb-4">
            {(run.stages_done || []).map((s) => <p key={s} className="flex items-center gap-2"><BadgeCheck size={12} className="text-emerald-400" /> {s}</p>)}
            {run.status === "RUNNING" && <p className="flex items-center gap-2 text-gold"><Loader2 size={12} className="animate-spin" /> Executing pipeline — companies are being discovered, verified, scored &amp; invited…</p>}
          </div>
          <div className="grid grid-cols-3 md:grid-cols-6 gap-2" data-testid="run-counts">
            {[["Discovered", run.counts?.discovered], ["Verified", run.counts?.verified], ["Contacts", run.counts?.contacts], ["Qualified", run.counts?.qualified], ["Invited", run.counts?.invited], ["Failed", run.counts?.failed]].map(([l, v]) => (
              <div key={l} className="bg-navy-900 border border-slate-800 rounded-sm p-3 text-center">
                <p className="text-[9px] font-mono uppercase tracking-widest text-slate-500">{l}</p>
                <p className="font-mono text-xl font-semibold text-slate-100 mt-1">{(v ?? 0).toLocaleString()}</p>
              </div>
            ))}
          </div>
          {run.status !== "RUNNING" && <p className="text-[11px] font-mono text-emerald-400 mt-4" data-testid="run-complete-line">ACQUISITION RUN {run.status} — {run.result?.created ?? run.counts?.discovered ?? 0} new companies processed ({run.result?.skipped_duplicates ?? 0} duplicates skipped)</p>}
        </div>
      )}

      {/* METRICS */}
      <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-3 mb-8" data-testid="acquisition-metrics">
        {METRIC_CARDS.map(([label, value, conv], i) => (
          <div key={label} className="card-surface p-4 relative" data-testid={`metric-${label.toLowerCase().replace(/[\s()≥]+/g, "-")}`}>
            <p className="text-[9px] font-mono uppercase tracking-widest text-slate-500 leading-snug">{label}</p>
            <p className="font-mono text-2xl md:text-3xl font-semibold text-slate-100 mt-2">{value.toLocaleString()}</p>
            {conv != null && <p className="text-[10px] font-mono text-emerald-400 mt-1">↓ {conv}% conversion</p>}
          </div>
        ))}
      </div>

      {/* PIPELINE */}
      <div className="card-surface p-5 mb-8 grid grid-cols-1 gap-1.5 sm:flex sm:flex-wrap sm:items-center" data-testid="pipeline-strip">
        {PIPELINE.map(([Icon, label], i) => {
          const count = (prospects || []).filter(STAGE_MATCH[label]).length;
          const active = stageFilter === label;
          return (
            <span key={label} className="flex items-center gap-2">
              <button onClick={() => setStageFilter(active ? "" : label)} data-testid={`stage-${label.toLowerCase().replace(/\s+/g, "-")}`}
                className={`flex items-center gap-2 border rounded-sm px-2.5 py-1.5 text-[9px] font-mono uppercase tracking-widest w-full sm:w-auto transition-colors ${active ? "border-gold text-gold bg-gold/10" : "border-slate-700 text-slate-300 hover:border-slate-500"}`}>
                <Icon size={12} className="text-gold shrink-0" />{label}
                <span className={`font-semibold ${active ? "text-gold" : "text-slate-500"}`}>{count.toLocaleString()}</span>
              </button>
              {i < PIPELINE.length - 1 && <ArrowRight size={11} className="text-slate-600 rotate-90 sm:rotate-0 mx-auto sm:mx-0" />}
            </span>
          );
        })}
        {stageFilter && <button onClick={() => setStageFilter("")} data-testid="stage-filter-clear" className="text-[9px] font-mono uppercase tracking-widest text-techblue-light hover:text-slate-200 px-2">Clear filter ×</button>}
      </div>

      {/* TODAY — LIVE ACTIVITY */}
      {metrics?.today && (
        <div className="card-surface p-5 mb-8" data-testid="today-metrics">
          <p className="text-[10px] font-mono uppercase tracking-widest text-gold mb-3 flex items-center gap-2"><Zap size={13} /> Today — Live Activity</p>
          <div className="grid grid-cols-2 sm:grid-cols-5 xl:grid-cols-10 gap-2">
            {[["Discovered", metrics.today.discovered], ["Verified", metrics.today.verified], ["Qualified", metrics.today.qualified], ["Invited", metrics.today.invited], ["Delivered", metrics.today.delivered], ["Opened", metrics.today.opened], ["Clicked", metrics.today.clicked], ["Replies", metrics.today.replies], ["Requests", metrics.today.requests], ["Members", metrics.today.members]].map(([l, v]) => (
              <div key={l} className="bg-navy-900 border border-slate-800 rounded-sm p-2.5 text-center">
                <p className="text-[8px] font-mono uppercase tracking-widest text-slate-500">{l}</p>
                <p className="font-mono text-lg font-semibold text-slate-100 mt-0.5">{(v ?? 0).toLocaleString()}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* EMAIL DELIVERY + SAFETY GATE */}
      {metrics?.email && (
        <div className="grid lg:grid-cols-2 gap-5 mb-8">
          <div className="card-surface p-5" data-testid="email-delivery-panel">
            <p className="text-[10px] font-mono uppercase tracking-widest text-gold mb-3 flex items-center gap-2"><Mail size={13} /> Email Delivery Engine</p>
            <div className={`flex items-center gap-2 mb-4 text-xs font-mono ${metrics.email.live ? "text-emerald-400" : "text-amber-400"}`} data-testid="email-engine-status">
              <span className={`w-2 h-2 rounded-full ${metrics.email.live ? "bg-emerald-400" : "bg-amber-400"} animate-pulse`} />
              {metrics.email.live ? "PRODUCTION READY" : "SANDBOX — AUTO-SENDING PAUSED"}
            </div>
            <dl className="grid grid-cols-2 gap-x-6 gap-y-2.5 text-xs">
              <div><dt className="text-slate-500 font-mono text-[9px] uppercase tracking-widest">Provider</dt><dd className="text-slate-200 mt-0.5">{metrics.email.provider}</dd></div>
              <div><dt className="text-slate-500 font-mono text-[9px] uppercase tracking-widest">Sending Domain</dt><dd className="text-slate-200 mt-0.5">{metrics.email.sending_domain}</dd></div>
              <div><dt className="text-slate-500 font-mono text-[9px] uppercase tracking-widest">From Address</dt><dd className="text-slate-200 mt-0.5">{metrics.email.from_address}</dd></div>
              <div><dt className="text-slate-500 font-mono text-[9px] uppercase tracking-widest">Delivered / Opened / Clicked</dt><dd className="text-slate-200 mt-0.5 font-mono">{metrics.delivery.delivered} / {metrics.delivery.opened} / {metrics.delivery.clicked}{metrics.delivery.bounced ? ` · ${metrics.delivery.bounced} bounced` : ""}</dd></div>
            </dl>
            {!metrics.email.live && <p className="text-[10px] text-amber-400/80 mt-4 font-mono">Fix: create a Resend account, verify 10x-global.com, then add RESEND_API_KEY to the backend environment.</p>}
          </div>
          <div className="card-surface p-5" data-testid="email-safety-gate">
            <p className="text-[10px] font-mono uppercase tracking-widest text-gold mb-3 flex items-center gap-2"><ShieldCheck size={13} /> Email Safety Gate</p>
            <div className="space-y-1.5 text-xs">
              {[
                ["Sending domain authenticated", metrics.safety_gate.domain_authenticated, ""],
                ["Provider connected", metrics.safety_gate.provider_connected, ""],
                ["SPF / DKIM / DMARC", metrics.safety_gate.domain_authenticated, metrics.safety_gate.spf_dkim_dmarc],
                ["Recipient suppression enforced", metrics.safety_gate.suppression_active, ""],
                ["Email validation active", metrics.safety_gate.email_validation_active, ""],
                ["Duplicate invitation protection", metrics.safety_gate.duplicate_protection_active, ""],
              ].map(([label, ok, note]) => (
                <p key={label} className="flex items-center justify-between gap-3">
                  <span className={`flex items-center gap-2 ${ok ? "text-slate-300" : "text-amber-400"}`}><BadgeCheck size={12} className={ok ? "text-emerald-400" : "text-amber-400"} /> {label}</span>
                  {note && !ok ? <span className="text-[9px] font-mono text-slate-500 text-right">{note}</span> : null}
                </p>
              ))}
            </div>
            <p className={`mt-4 text-[10px] font-mono uppercase tracking-widest ${metrics.safety_gate.auto_sending === "LIVE" ? "text-emerald-400" : "text-amber-400"}`} data-testid="auto-sending-status">AUTO-SENDING {metrics.safety_gate.auto_sending}</p>
          </div>
        </div>
      )}

      {/* AI RECOMMENDATIONS + FORECAST */}
      <div className="grid lg:grid-cols-2 gap-5 mb-8">
        {recs && (
          <div className="card-surface p-5" data-testid="ai-recommendations">
            <p className="text-[10px] font-mono uppercase tracking-widest text-gold mb-3 flex items-center gap-2"><Lightbulb size={13} /> 10X AI Recommendation</p>
            {recs.insight && <p className="text-xs text-slate-300 leading-relaxed mb-4 border-l-2 border-gold/50 pl-3">{recs.insight}</p>}
            <div className="space-y-2.5">
              {recs.recommendations.map((r) => (
                <div key={r.text} className="flex items-center justify-between gap-3 bg-navy-900 border border-slate-800 rounded-sm px-3 py-2.5">
                  <p className="text-xs text-slate-400">{r.text}</p>
                  <button onClick={() => runRecAction(r.key)} disabled={!!working} data-testid={`rec-action-${r.key}`} className="btn-gold text-[10px] !px-3 !py-1.5 shrink-0 disabled:opacity-60">{r.action}</button>
                </div>
              ))}
              {!recs.recommendations.length && <p className="text-xs text-slate-500">No pending actions — the pipeline is clear.</p>}
            </div>
          </div>
        )}
        {forecast && (
          <div className="card-surface p-5" data-testid="forecast-panel">
            <p className="text-[10px] font-mono uppercase tracking-widest text-gold mb-1 flex items-center gap-2"><TrendingUp size={13} /> 30-Day Acquisition Forecast</p>
            <p className="text-[9px] font-mono uppercase tracking-widest text-amber-400 mb-3">{forecast.label} · based on last {forecast.basis_days} days</p>
            <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
              {[["Discoveries", forecast.expected.discoveries], ["Qualified", forecast.expected.qualified], ["Invitations", forecast.expected.invitations], ["Engaged", forecast.expected.engaged], ["Requests", forecast.expected.requests], ["New Members", forecast.expected.members]].map(([l, v]) => (
                <div key={l} className="bg-navy-900 border border-slate-800 rounded-sm p-2.5 text-center">
                  <p className="text-[8px] font-mono uppercase tracking-widest text-slate-500">{l}</p>
                  <p className="font-mono text-lg font-semibold text-slate-100 mt-0.5">{(v ?? 0).toLocaleString()}</p>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* CAMPAIGNS */}
      {runs.length > 0 && (
        <div className="card-surface p-5 mb-8" data-testid="campaigns-panel">
          <p className="text-[10px] font-mono uppercase tracking-widest text-gold mb-3 flex items-center gap-2"><CalendarClock size={13} /> Campaigns</p>
          <div className="space-y-2">
            {runs.slice(0, 6).map((r) => (
              <div key={r.run_id} className="flex flex-wrap items-center justify-between gap-3 bg-navy-900 border border-slate-800 rounded-sm px-3 py-2.5" data-testid={`campaign-${r.run_id}`}>
                <div className="min-w-0">
                  <p className="text-xs font-semibold text-slate-200 truncate">{r.name} <span className="text-[9px] font-mono text-slate-500 uppercase ml-2">{r.source} · {(r.created_at || "").slice(0, 10)}</span></p>
                  <p className="text-[10px] font-mono text-slate-500 mt-0.5">discovered {r.counts?.discovered ?? 0} · verified {r.counts?.verified ?? 0} · qualified {r.counts?.qualified ?? 0} · invited {r.counts?.invited ?? 0}</p>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <span className={`text-[9px] font-mono uppercase tracking-widest border rounded-sm px-2 py-0.5 ${r.status === "RUNNING" ? "text-techblue-light border-techblue/40" : r.status === "COMPLETE" ? "text-emerald-400 border-emerald-500/40" : r.status === "STOPPED" ? "text-amber-400 border-amber-500/40" : "text-red-400 border-red-500/40"}`}>{r.status}</span>
                  {r.status === "RUNNING" && <button onClick={() => stopRun(r.run_id)} data-testid={`campaign-stop-${r.run_id}`} className="btn-outline text-[10px] !px-3 !py-1"><Square size={10} /> STOP</button>}
                  <button onClick={() => pollRun(r.run_id)} data-testid={`campaign-view-${r.run_id}`} className="btn-outline text-[10px] !px-3 !py-1">VIEW</button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* AUTOPILOT */}
      {settings && (
        <div className="card-surface hairline-gold p-5 mb-8 grid md:grid-cols-3 gap-5" data-testid="autopilot-panel">
          <div className="flex items-center justify-between gap-4">
            <div>
              <p className="text-xs font-semibold text-slate-100">AUTO-INVITE VERIFIED COMPANIES</p>
              <p className="text-[10px] text-slate-500 mt-1">Invitation emails go out the moment a company qualifies</p>
            </div>
            <button onClick={() => toggle("auto_invite", !settings.auto_invite)} data-testid="toggle-auto-invite"
              className={`w-11 h-6 rounded-full transition-colors shrink-0 relative ${settings.auto_invite ? "bg-gold" : "bg-navy-700 border border-slate-600"}`}>
              <span className={`absolute top-0.5 w-5 h-5 rounded-full bg-white transition-all ${settings.auto_invite ? "left-[22px]" : "left-0.5"}`} />
            </button>
          </div>
          <div className="flex items-center justify-between gap-4">
            <div>
              <p className="text-xs font-semibold text-slate-100">AUTO-APPROVE AFTER OTP</p>
              <p className="text-[10px] text-slate-500 mt-1">Invited members activate without manual approval</p>
            </div>
            <button onClick={() => toggle("auto_approve", !settings.auto_approve)} data-testid="toggle-auto-approve"
              className={`w-11 h-6 rounded-full transition-colors shrink-0 relative ${settings.auto_approve ? "bg-gold" : "bg-navy-700 border border-slate-600"}`}>
              <span className={`absolute top-0.5 w-5 h-5 rounded-full bg-white transition-all ${settings.auto_approve ? "left-[22px]" : "left-0.5"}`} />
            </button>
          </div>
          <div className="flex items-center justify-between gap-4">
            <div>
              <p className="text-xs font-semibold text-slate-100">AI SCORE THRESHOLD</p>
              <p className="text-[10px] text-slate-500 mt-1">Minimum score for automatic steps</p>
            </div>
            <input type="number" min="40" max="95" value={settings.score_threshold}
              onChange={(e) => setSettings({ ...settings, score_threshold: Number(e.target.value) })}
              onBlur={(e) => toggle("score_threshold", Number(e.target.value))}
              data-testid="score-threshold-input" className="input-dark !w-20 !py-2 text-center font-mono" />
          </div>
          <div className="flex items-center justify-between gap-4">
            <div>
              <p className="text-xs font-semibold text-slate-100">AUTO DISCOVERY (DAILY CRON)</p>
              <p className="text-[10px] text-slate-500 mt-1">Scheduled engine discovers new companies daily</p>
            </div>
            <button onClick={() => toggle("auto_discovery", !settings.auto_discovery)} data-testid="toggle-auto-discovery"
              className={`w-11 h-6 rounded-full transition-colors shrink-0 relative ${settings.auto_discovery ? "bg-gold" : "bg-navy-700 border border-slate-600"}`}>
              <span className={`absolute top-0.5 w-5 h-5 rounded-full bg-white transition-all ${settings.auto_discovery ? "left-[22px]" : "left-0.5"}`} />
            </button>
          </div>
          <div className="flex items-center justify-between gap-4">
            <div>
              <p className="text-xs font-semibold text-slate-100">AUTO FOLLOW-UP</p>
              <p className="text-[10px] text-slate-500 mt-1">Follow-up invitations if no response (opt-out safe)</p>
            </div>
            <button onClick={() => toggle("follow_ups_enabled", !settings.follow_ups_enabled)} data-testid="toggle-follow-ups"
              className={`w-11 h-6 rounded-full transition-colors shrink-0 relative ${settings.follow_ups_enabled ? "bg-gold" : "bg-navy-700 border border-slate-600"}`}>
              <span className={`absolute top-0.5 w-5 h-5 rounded-full bg-white transition-all ${settings.follow_ups_enabled ? "left-[22px]" : "left-0.5"}`} />
            </button>
          </div>
          <div className="flex items-center justify-between gap-4">
            <div>
              <p className="text-xs font-semibold text-slate-100">FOLLOW-UP RULES</p>
              <p className="text-[10px] text-slate-500 mt-1">Max per company · days between</p>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <input type="number" min="0" max="5" value={settings.follow_up_max}
                onChange={(e) => setSettings({ ...settings, follow_up_max: Number(e.target.value) })}
                onBlur={(e) => toggle("follow_up_max", Number(e.target.value))}
                data-testid="followup-max-input" className="input-dark !w-16 !py-2 text-center font-mono" />
              <input type="number" min="1" max="14" value={settings.follow_up_interval_days}
                onChange={(e) => setSettings({ ...settings, follow_up_interval_days: Number(e.target.value) })}
                onBlur={(e) => toggle("follow_up_interval_days", Number(e.target.value))}
                data-testid="followup-interval-input" className="input-dark !w-16 !py-2 text-center font-mono" />
            </div>
          </div>
        </div>
      )}

      {/* DISCOVERY ENGINE */}
      <div className="card-surface p-5 mb-8" data-testid="discovery-engine">
        <p className="text-[10px] font-mono uppercase tracking-widest text-gold mb-3 flex items-center gap-2"><Radar size={13} /> Automatic Company Discovery Engine</p>
        <div className="grid md:grid-cols-6 gap-3 items-end">
          <div>
            <label className="block text-[10px] font-mono uppercase tracking-widest text-slate-500 mb-1.5">Source</label>
            <select value={disc.source || "llm"} onChange={(e) => setDisc({ ...disc, source: e.target.value })} data-testid="discover-source" className="input-dark !py-2.5">
              <option value="llm" className="bg-navy-900">10X AI</option>
              <option value="google" className="bg-navy-900">GOOGLE PLACES</option>
            </select>
          </div>
          <div>
            <label className="block text-[10px] font-mono uppercase tracking-widest text-slate-500 mb-1.5">Country</label>
            <select value={disc.country} onChange={(e) => setDisc({ ...disc, country: e.target.value })} data-testid="discover-country" className="input-dark !py-2.5">
              {COUNTRIES.map((c) => <option key={c} className="bg-navy-900">{c}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-[10px] font-mono uppercase tracking-widest text-slate-500 mb-1.5">Industry</label>
            <select value={disc.industry} onChange={(e) => setDisc({ ...disc, industry: e.target.value })} data-testid="discover-industry" className="input-dark !py-2.5">
              {INDUSTRIES.map((c) => <option key={c} className="bg-navy-900">{c}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-[10px] font-mono uppercase tracking-widest text-slate-500 mb-1.5">Company Size</label>
            <select value={disc.size} onChange={(e) => setDisc({ ...disc, size: e.target.value })} data-testid="discover-size" className="input-dark !py-2.5">
              {SIZES.map((c) => <option key={c} className="bg-navy-900">{c}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-[10px] font-mono uppercase tracking-widest text-slate-500 mb-1.5">Opportunity Focus</label>
            <input value={disc.filters} onChange={(e) => setDisc({ ...disc, filters: e.target.value })} placeholder="Funding, export, distribution…" data-testid="discover-filters" className="input-dark !py-2.5" />
          </div>
          <button onClick={runDiscovery} disabled={!!working} data-testid="run-discovery-btn" className="btn-gold !py-3 text-xs disabled:opacity-60">
            {working === "discover" ? <><Loader2 size={14} className="animate-spin" /> DISCOVERING…</> : "RUN 10X ACQUISITION ENGINE →"}
          </button>
        </div>
        <p className="text-[10px] text-slate-600 mt-3">Engine produces real companies only — emails are never fabricated. Discovered companies are verified & scored, then wait for a public business contact (LOW CONFIDENCE contacts are never auto-invited). GOOGLE PLACES source pulls Google-verified businesses with their official websites (requires GOOGLE_PLACES_API_KEY).</p>
      </div>

      {/* BATCH */}
      <div className="card-surface p-5 mb-8" data-testid="batch-import">
        <p className="text-[10px] font-mono uppercase tracking-widest text-gold mb-3">Batch Acquisition — one per line: Company | Website | Email | Industry</p>
        <div className="grid md:grid-cols-[1fr_auto] gap-3">
          <textarea rows={3} value={batch} onChange={(e) => setBatch(e.target.value)} data-testid="batch-input"
            placeholder={"ABC Industries | abc.com | contact@abc.com | Manufacturing\nXYZ Logistics | xyz.com | hello@xyz.com | Logistics"}
            className="input-dark resize-none font-mono text-xs" />
          <button onClick={runBatch} disabled={!!working || !batch.trim()} data-testid="batch-run-btn" className="btn-gold text-xs !px-6 self-end disabled:opacity-60">
            {working === "batch" ? <><Loader2 size={14} className="animate-spin" /> RUNNING…</> : "RUN BATCH PIPELINE"}
          </button>
        </div>
      </div>

      {/* GOVERNMENT SOURCES */}
      <div className="card-surface p-5 mb-8" data-testid="gov-import">
        <p className="text-[10px] font-mono uppercase tracking-widest text-gold mb-3">Government Sources Import — APEDA · MPEDA · Trade Connect · FIEO — one per line: Company | Website (optional) | Email (optional) | Industry | City</p>
        <div className="grid md:grid-cols-[auto_1fr_auto] gap-3 items-end">
          <select value={govSource} onChange={(e) => setGovSource(e.target.value)} data-testid="gov-source-select" className="input-dark !w-auto !py-2.5">
            {["APEDA", "MPEDA", "TRADE CONNECT", "FIEO", "OTHER"].map((s) => <option key={s} value={s} className="bg-navy-900">{s}</option>)}
          </select>
          <textarea rows={3} value={govRows} onChange={(e) => setGovRows(e.target.value)} data-testid="gov-import-input"
            placeholder={"ABC Agro Exports | abcagro.com | | Agriculture | Mumbai\nOcean Fresh Seafoods | | info@oceanfresh.in | Seafood | Kochi"}
            className="input-dark resize-none font-mono text-xs" />
          <div className="flex flex-col gap-2">
            <button onClick={runGovImport} disabled={!!working || !govRows.trim()} data-testid="gov-import-run-btn" className="btn-gold text-xs !px-6 disabled:opacity-60">
              {working === "gov" ? <><Loader2 size={14} className="animate-spin" /> IMPORTING…</> : "IMPORT & RUN PIPELINE"}
            </button>
            <button onClick={syncMpeda} disabled={!!working} data-testid="mpeda-sync-btn" className="btn-outline text-xs !px-6 disabled:opacity-60">
              {working === "mpeda" ? <><Loader2 size={14} className="animate-spin" /> SYNCING…</> : "AUTO-SYNC MPEDA REGISTRY"}
            </button>
          </div>
        </div>
        <p className="text-[10px] text-slate-600 mt-3">Copy exporter rows from the official directories (apeda.gov.in → Exporter Directory, e-mpeda.nic.in → registered exporters, trade.gov.in → Source from India). Missing websites are found by 10X AI and verified; missing emails are enriched only from the company's own official website — never fabricated.</p>
      </div>

      {/* SINGLE ADD */}
      <form onSubmit={addProspect} className="card-surface hairline-gold p-6 mb-8 grid md:grid-cols-5 gap-4 items-end" data-testid="prospect-form">
        <div>
          <label className="block text-[10px] font-mono uppercase tracking-widest text-slate-500 mb-1.5">Company Name</label>
          <input required value={form.company_name} onChange={(e) => setForm({ ...form, company_name: e.target.value })} placeholder="Acme Industries" data-testid="prospect-company-input" className="input-dark" />
        </div>
        <div>
          <label className="block text-[10px] font-mono uppercase tracking-widest text-slate-500 mb-1.5">Official Website</label>
          <input required value={form.website} onChange={(e) => setForm({ ...form, website: e.target.value })} placeholder="acme.com" data-testid="prospect-website-input" className="input-dark" />
        </div>
        <div>
          <label className="block text-[10px] font-mono uppercase tracking-widest text-slate-500 mb-1.5">Industry</label>
          <input value={form.industry} onChange={(e) => setForm({ ...form, industry: e.target.value })} placeholder="Manufacturing" data-testid="prospect-industry-input" className="input-dark" />
        </div>
        <div>
          <label className="block text-[10px] font-mono uppercase tracking-widest text-slate-500 mb-1.5">Business Contact Email</label>
          <input type="email" required value={form.contact_email} onChange={(e) => setForm({ ...form, contact_email: e.target.value })} placeholder="contact@acme.com" data-testid="prospect-email-input" className="input-dark" />
        </div>
        <button type="submit" disabled={!!working} data-testid="prospect-add-btn" className="btn-gold !py-3 text-xs disabled:opacity-60">
          {working === "single" ? <><Loader2 size={14} className="animate-spin" /> RUNNING PIPELINE…</> : "ADD — RUN PIPELINE AUTOMATICALLY"}
        </button>
      </form>

      {/* FAILURES CENTER */}
      {failures.length > 0 && (
        <div className="card-surface border-red-500/30 p-6 mb-8" data-testid="failures-center">
          <div className="flex items-center justify-end mb-3">
            <button onClick={retryAll} disabled={!!working} data-testid="retry-all-btn" className="btn-outline text-[10px] !px-4 !py-1.5 disabled:opacity-60">
              {working === "retryall" ? <Loader2 size={11} className="animate-spin" /> : <RotateCcw size={11} />} RETRY FAILED OPERATIONS
            </button>
          </div>
          <p className="text-[10px] font-mono uppercase tracking-widest text-red-400 mb-4 flex items-center gap-2"><AlertTriangle size={13} /> Failed Operations Center ({failures.length})</p>
          <div className="space-y-3">
            {failures.map((p) => (
              <div key={p.token} className="flex flex-wrap items-center gap-3 border border-slate-800 rounded-sm p-3.5" data-testid={`failure-${p.token}`}>
                <div className="flex-1 min-w-[200px]">
                  <p className="text-sm font-semibold text-slate-100">{p.company_name}</p>
                  <p className="text-[10px] font-mono text-slate-500">{p.website} · {p.ai_score}/100</p>
                  <p className="text-[10px] text-red-400 mt-1">{p.failure_reason || (p.status === "VERIFICATION_FAILED" ? "Website unreachable" : p.status === "NEEDS_CONTACT" ? "Public business contact email needed" : p.status)}</p>
                </div>
                <span className={`border rounded-sm px-2 py-0.5 text-[10px] font-mono uppercase tracking-wider ${STATUS_CLS[p.status]}`}>{p.status}</span>
                {p.status === "NEEDS_CONTACT" ? (
                  <div className="flex gap-2 items-center">
                    <input value={contactDraft[p.token] || ""} onChange={(e) => setContactDraft({ ...contactDraft, [p.token]: e.target.value })}
                      placeholder="public@business-email.com" data-testid={`contact-input-${p.token}`} className="input-dark !w-52 !py-2 text-xs" />
                    <button onClick={() => saveContact(p.token)} disabled={!!inviting} data-testid={`contact-save-${p.token}`}
                      className="text-[10px] font-mono uppercase tracking-wider bg-gold/15 border border-gold/50 text-gold rounded-sm px-3 py-2 hover:bg-gold/25 disabled:opacity-40">SAVE & RUN</button>
                  </div>
                ) : (
                  <button onClick={() => retry(p.token)} disabled={!!inviting} data-testid={`retry-${p.token}`}
                    className="text-[10px] font-mono uppercase tracking-wider border border-techblue/50 text-techblue-light rounded-sm px-3 py-2 hover:bg-techblue/10 disabled:opacity-40 flex items-center gap-1.5">
                    {inviting === p.token ? <Loader2 size={11} className="animate-spin" /> : <RotateCcw size={11} />} RETRY
                  </button>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* PROSPECTS + ACTIVITY */}
      <div className="grid xl:grid-cols-[1fr_360px] gap-6">
        <div className="card-surface p-6 overflow-x-auto" data-testid="prospects-table">
          <p className="text-[10px] font-mono uppercase tracking-widest text-gold mb-4">Acquisition Database ({visibleProspects.length}{stageFilter ? ` — filtered: ${stageFilter}` : ""})</p>
          {visibleProspects.length === 0 ? <p className="text-sm text-slate-500">{stageFilter ? "No companies at this pipeline stage — click the stage again to clear the filter." : "No companies yet — run the discovery engine or add one above."}</p> : (
            <table className="w-full text-sm min-w-[960px]">
              <thead>
                <tr className="text-left text-[10px] font-mono uppercase tracking-widest text-slate-500 border-b border-slate-800">
                  <th className="pb-3 pr-4">Company</th><th className="pb-3 pr-4">Website</th><th className="pb-3 pr-4">Verification</th><th className="pb-3 pr-4">10X AI Score</th><th className="pb-3 pr-4">Status</th><th className="pb-3 pr-4">Delivery</th><th className="pb-3 pr-4">Invite Link</th><th className="pb-3">Action</th>
                </tr>
              </thead>
              <tbody>
                {visibleProspects.map((p) => (
                  <tr key={p.token} className="border-b border-slate-800/60 text-slate-300" data-testid={`prospect-row-${p.token}`}>
                    <td className="py-3.5 pr-4">
                      <p className="text-xs font-semibold text-slate-100">{p.company_name}</p>
                      <p className="text-[10px] font-mono text-slate-500">
                        {p.contact_email || "no contact yet"}
                        {p.contact_confidence && <span className={`ml-1.5 border rounded-sm px-1 py-px text-[8px] uppercase ${p.contact_confidence === "HIGH" ? "text-emerald-400 border-emerald-500/40" : p.contact_confidence === "MEDIUM" ? "text-amber-400 border-amber-500/40" : "text-slate-500 border-slate-600"}`}>{p.contact_confidence}</span>}
                      </p>
                    </td>
                    <td className="py-3.5 pr-4 font-mono text-xs text-slate-400">{p.website}</td>
                    <td className="py-3.5 pr-4">
                      {p.website_verified
                        ? <span className="text-[10px] font-mono uppercase tracking-wider text-emerald-400 border border-emerald-500/40 bg-emerald-500/10 rounded-sm px-2 py-0.5">VERIFIED {p.website_status}</span>
                        : <span className="text-[10px] font-mono uppercase tracking-wider text-red-400 border border-red-500/40 bg-red-500/10 rounded-sm px-2 py-0.5">FAILED</span>}
                    </td>
                    <td className="py-3.5 pr-4">
                      <span className="font-mono text-gold font-semibold">{p.ai_score}</span><span className="text-xs text-slate-500">/100</span>
                    </td>
                    <td className="py-3.5 pr-4">
                      <span className={`border rounded-sm px-2 py-0.5 text-[10px] font-mono uppercase tracking-wider ${STATUS_CLS[p.status] || "text-slate-400 border-slate-600"}`}>{p.status}</span>
                      {p.invited_by?.startsWith("auto:") && <p className="text-[8px] font-mono text-gold mt-1">AUTOPILOT</p>}
                      {p.auto_approved && <p className="text-[8px] font-mono text-emerald-400 mt-1">AUTO-APPROVED</p>}
                    </td>
                    <td className="py-3.5 pr-4" data-testid={`delivery-${p.token}`}>
                      {p.delivery_status
                        ? <span className={`text-[10px] font-mono uppercase tracking-wider border rounded-sm px-2 py-0.5 ${["BOUNCED", "FAILED", "COMPLAINED"].includes(p.delivery_status) ? "text-red-400 border-red-500/40 bg-red-500/10" : p.delivery_status === "DELIVERED" ? "text-emerald-400 border-emerald-500/40 bg-emerald-500/10" : "text-cyan-300 border-cyan-400/40 bg-cyan-400/10"}`}>{p.delivery_status}</span>
                        : <span className="text-[10px] font-mono text-slate-600">—</span>}
                      {p.follow_ups_sent > 0 && <p className="text-[8px] font-mono text-slate-500 mt-1">FOLLOW-UP {p.follow_ups_sent}×</p>}
                    </td>
                    <td className="py-3.5 pr-4"><Link to={`/invite/${p.token}`} target="_blank" className="text-[10px] font-mono text-techblue-light hover:text-gold">/invite/{p.token.slice(0, 6)}…</Link></td>
                    <td className="py-3.5">
                      <button onClick={() => invite(p.token, p.company_name)} disabled={!!inviting || p.status === "MEMBER" || p.status === "INVITED"} data-testid={`invite-${p.token}`}
                        className="text-[10px] font-mono uppercase tracking-wider bg-gold/15 border border-gold/50 text-gold rounded-sm px-3 py-1.5 hover:bg-gold/25 disabled:opacity-40 flex items-center gap-1.5">
                        {inviting === p.token ? <Loader2 size={11} className="animate-spin" /> : <Send size={11} />} INVITE
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        <div className="card-surface p-6" data-testid="activity-stream">
          <p className="text-[10px] font-mono uppercase tracking-widest text-gold mb-4 flex items-center gap-2"><Activity size={13} /> Live Activity Stream</p>
          <ul className="space-y-3 max-h-[560px] overflow-y-auto pr-1">
            {activity.length === 0 && <p className="text-xs text-slate-500">No activity yet — run the engine.</p>}
            {activity.map((ev, i) => {
              const Icon = EVENT_ICON(ev.type);
              const failed = ev.type.includes("failed");
              return (
                <li key={i} className="flex items-start gap-2.5 text-xs" data-testid={`activity-${i}`}>
                  <Icon size={13} className={`${failed ? "text-red-400" : "text-gold"} shrink-0 mt-0.5`} />
                  <div className="min-w-0">
                    <p className={`${failed ? "text-red-300" : "text-slate-300"} leading-snug`}>{ev.detail}</p>
                    <p className="text-[9px] font-mono text-slate-600 mt-0.5">{ev.ts?.slice(11, 19)} · {ev.type}</p>
                  </div>
                </li>
              );
            })}
          </ul>
        </div>
      </div>
    </div>
  );
}
